import React, { useState } from 'react';
import { ExperimentDetails, Metric } from '@shared/types';
import useMetricRowData from './hooks/useMetricRowData';
import useMetricDecimalsPrecision from './hooks/useMetricDecimalsPrecision';
import { useSelector } from 'react-redux';
import { isUserAllowedToEditProject } from '@/reducers/projectsReducer';
import useTableRowSearch from '@shared/hooks/useTableRowSearch';
import DeleteMetricParamModal from './modals/DeleteMetricParamModal';
import { debounce } from 'lodash';
import ExperimentBar from '../ExperimentBar';
import { Slider } from '@design-system-outdated/components';
import useDeleteMetricApi from './hooks/useDeleteMetricApi';
import { TextButton } from '@design-system/components';
import { DSPlusIcon } from '@design-system/icons';
import AddMetricParamModal from './modals/AddMetricParamModal';
import EditMetricParamModal from './modals/EditMetricParamModal';
import MetricsTableNew from './MetricsTable';
import useMetricModalState from './hooks/useMetricModalState';
import useMetricTabBI from './hooks/useMetricTabBI';
import { experimentEvents } from '@/constants/trackingEventTypes';
import {
  MAX_DECIMAL_PRECISION,
  MIN_DECIMAL_PRECISION
} from '@experiment-management-shared/constants/experimentGridConstants';
import {
  DATETIME_CELL_KEYS,
  METRIC_TABLE_COLUMN_BANDS,
  METRIC_TABLE_COLUMNS
} from './metricsTableConfig';
import FileSaver from 'file-saver';
import Papa from 'papaparse';
import DownloadParamsButton from '@shared/components/DownloadParamsButton/DownloadParamsButton';

const CSV_FILE_EXTENSION = 'csv';

type MetricsTableContainerProps = {
  items: Metric[];
  experiment: ExperimentDetails;
  isFetching: boolean;
};
const MetricsTableContainer: React.FC<MetricsTableContainerProps> = ({
  items,
  experiment,
  isFetching
}) => {
  const { sendMetricTabBI } = useMetricTabBI();
  const addMetricModalState = useMetricModalState();
  const editMetricModalState = useMetricModalState();
  const experimentKey = experiment.experimentKey;
  const canEdit = useSelector(isUserAllowedToEditProject);
  const { searchText, handleSearch, resetSearch } = useTableRowSearch();
  const { rowData } = useMetricRowData({
    items,
    searchText
  });

  const { onDeleteMetric, deleteModalConfig } = useDeleteMetricApi({
    experimentKey
  });

  const {
    decimalsPrecision,
    onChangeDecimalsPrecision
  } = useMetricDecimalsPrecision();

  const [
    tempDecimalsPrecision,
    onChangeTempDecimalsPrecision
  ] = useState<number>();
  const debouncedOnChangeDecimalsPrecision = debounce(
    onChangeDecimalsPrecision,
    300
  );

  const exportCSV = () => {
    const dateToIsoString = (date?: number) => {
      return date ? new Date(date).toISOString() : '';
    };

    const fileName = `${experiment.Name}_metrics.${CSV_FILE_EXTENSION}`;
    const columnBandMap: Record<string, string> = {};
    METRIC_TABLE_COLUMN_BANDS.forEach(band => {
      band?.children?.forEach(child => {
        if (child.columnName && band.title?.trim()) {
          columnBandMap[child.columnName] = `${band.title}_`;
        }
      });
    });

    const columnsWithTitle = METRIC_TABLE_COLUMNS.filter(col =>
      col.title.trim()
    );

    const metricsDataToExport = [
      columnsWithTitle.map(col =>
        `${columnBandMap[col.name] || ''}${col.title}`.toUpperCase()
      ),
      ...items.map(row =>
        columnsWithTitle.map(col =>
          DATETIME_CELL_KEYS.includes(col.name)
            ? dateToIsoString(row[col.name as keyof Metric] as number)
            : row[col.name as keyof Metric]
        )
      )
    ];

    const metricsDataCsvString = Papa.unparse(metricsDataToExport);

    FileSaver.saveAs(
      new Blob([metricsDataCsvString], { type: `text/${CSV_FILE_EXTENSION}` }),
      fileName
    );
  };

  const addMetric = () => {
    addMetricModalState.onOpenModal({
      callback: () => {
        sendMetricTabBI(
          experiment,
          experimentEvents.EXPERIMENT_DETAILS_ADD_METRIC_CLICKED
        );
      }
    });
  };
  const editMetric = (row: Metric) => {
    editMetricModalState.onOpenModal({
      defaultFormData: row,
      callback: () => {
        sendMetricTabBI(
          experiment,
          experimentEvents.EXPERIMENT_DETAILS_EDIT_METRIC_CLICKED
        );
      }
    });
  };

  const isEmptyTab = !items.length;

  return (
    <div className="table-tab-container">
      <ExperimentBar
        rightContainerChildren={
          <ExperimentBar.ActionList>
            <DownloadParamsButton
              onDownload={exportCSV}
              isDownloadButtonDisabled={isEmptyTab}
              tooltipContent="Download as a CSV"
            />
            <TextButton
              onClick={addMetric}
              PrefixIcon={<DSPlusIcon />}
              disabled={!canEdit}
            >
              Add metric
            </TextButton>
          </ExperimentBar.ActionList>
        }
      >
        <ExperimentBar.Search
          searchText={searchText}
          setSearchText={handleSearch}
          placeholder="Name (regex)"
          disabled={isEmptyTab}
        />

        <ExperimentBar.Custom
          label="Decimal precision"
          component={
            <Slider
              min={MIN_DECIMAL_PRECISION}
              max={MAX_DECIMAL_PRECISION}
              step={1}
              value={tempDecimalsPrecision ?? decimalsPrecision}
              onChange={onChangeTempDecimalsPrecision}
              onChangeCommitted={debouncedOnChangeDecimalsPrecision}
            />
          }
        />
      </ExperimentBar>

      <AddMetricParamModal
        rowData={rowData}
        experiment={experiment}
        onClose={addMetricModalState.onCloseModal}
        isOpened={addMetricModalState.isOpened}
      />
      <EditMetricParamModal
        experiment={experiment}
        onClose={editMetricModalState.onCloseModal}
        isOpened={editMetricModalState.isOpened}
        defaultFormData={editMetricModalState.defaultFormData}
      />
      <DeleteMetricParamModal {...deleteModalConfig} />

      <MetricsTableNew
        decimalsPrecision={decimalsPrecision}
        onDeleteMetric={onDeleteMetric}
        onEditMetric={editMetric}
        rowData={rowData}
        isFetching={isFetching}
        searchText={searchText}
        resetSearch={resetSearch}
      />
    </div>
  );
};

export default MetricsTableContainer;
